import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useParams} from "@tanstack/react-router";
import {CalendarDays, Clock, MapPin, Share2, Trophy} from "lucide-react";
import services from "@/services";

export const Route = createFileRoute('/sched/$token')({
    component: RouteComponent,
})

const dayLabel = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {weekday: 'long', day: 'numeric', month: 'long'});
const timeLabel = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});

function RouteComponent() {
    const {token} = useParams({from: '/sched/$token'});

    const q = useQuery({
        queryKey: ['player-schedule', token],
        queryFn:  () => services.publicEvent.playerSchedule(token),
        enabled:  !!token,
        retry:    false,
    });

    const share = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) await navigator.share({title: 'My schedule', url});
            else { await navigator.clipboard.writeText(url); addToast({color: 'success', title: 'Link copied'}); }
        } catch { /* dismissed */ }
    };

    if (q.isLoading) {
        return <div className="min-h-screen bg-[#06133e] flex items-center justify-center"><Spinner size="lg" color="white" /></div>;
    }
    if (q.isError || !q.data) {
        return (
            <div className="min-h-screen bg-[#06133e] flex flex-col items-center justify-center px-6 text-center text-white">
                <Trophy className="mb-3 text-white/40" size={40} />
                <h1 className="text-xl font-bold">Schedule not available</h1>
                <p className="mt-1 text-sm text-white/60">This link is invalid or has expired. Ask the organiser to resend it.</p>
            </div>
        );
    }

    const e = q.data;
    const matches: any[] = e.matches || [];
    // Group matches by calendar day for a readable agenda.
    const groups: {day: string; items: any[]}[] = [];
    for (const m of matches) {
        const day = m.scheduled_at ? dayLabel(m.scheduled_at) : 'TBD';
        let g = groups.find((x) => x.day === day);
        if (!g) { g = {day, items: []}; groups.push(g); }
        g.items.push(m);
    }
    const location = [e.tournament?.venue, e.tournament?.city].filter(Boolean).join(', ');

    return (
        <main className="min-h-screen bg-[#06133e] text-white">
            <div className="mx-auto w-11/12 max-w-2xl py-8">
                <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-5">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wider text-white/50">Your schedule</div>
                            <h1 className="mt-1 font-bebas text-3xl tracking-wide">{e.tournament?.title}</h1>
                            <div className="mt-1 text-sm text-white/70">{e.player?.name}</div>
                            {location && <div className="mt-1 flex items-center gap-1 text-xs text-white/50"><MapPin size={13} /> {location}</div>}
                        </div>
                        <button onClick={share} className="shrink-0 rounded-full border border-white/15 bg-white/5 p-2.5 hover:bg-white/10" aria-label="Share">
                            <Share2 size={18} />
                        </button>
                    </div>
                </div>

                {matches.length === 0 ? (
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/60">
                        <CalendarDays className="mx-auto mb-2 text-white/30" size={28} />
                        No matches scheduled yet — check back once the organiser publishes the draw.
                    </div>
                ) : (
                    <div className="space-y-5">
                        {groups.map((g) => (
                            <div key={g.day}>
                                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#7ba6ff]">
                                    <CalendarDays size={15} /> {g.day}
                                </div>
                                <div className="space-y-2">
                                    {g.items.map((m, i) => (
                                        <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-4">
                                            <div className="flex items-center justify-between gap-3 text-xs text-white/60">
                                                <span className="inline-flex items-center gap-1"><Clock size={13} /> {m.scheduled_at ? timeLabel(m.scheduled_at) : 'TBD'}</span>
                                                {m.court && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {m.court}</span>}
                                            </div>
                                            <div className="mt-2 text-[15px] font-medium leading-snug">
                                                {m.side_a} <span className="text-white/40">v</span> {m.side_b}
                                            </div>
                                            {m.finished && m.score && (
                                                <div className="mt-1 text-xs font-semibold text-[#14a653]">Final: {m.score}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-8 text-center text-xs text-white/40">Powered by ScorePal</div>
            </div>
        </main>
    );
}
